import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';

export default function BattlePlanRedirectScreen() {
  const { battlePlanId } = useLocalSearchParams<{ battlePlanId: string }>();
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { getSelectedBranch, getCampaigns } = useNavigationStore();
  const { cachedData } = useCampaignStore();

  useEffect(() => {
    const findCampaignAndRedirect = async () => {
      if (!battlePlanId) {
        setError('Missing battle plan ID');
        setLoading(false);
        return;
      }

      try {
        const selectedBranch = getSelectedBranch();
        if (!selectedBranch) {
          setError('No branch selected');
          setLoading(false);
          return;
        }

        const branchId = selectedBranch.branch_short_name;
        const campaigns = getCampaigns(branchId);
        
        if (!campaigns || campaigns.length === 0) {
          setError('No campaigns found for this branch');
          setLoading(false);
          return;
        }

        // Search through all campaigns to find which one contains this battle plan
        let foundCampaignId: string | null = null;
        
        for (const campaign of campaigns) {
          const campaignId = campaign.boundary_id;
          const campaignData = cachedData[branchId]?.campaigns?.[campaignId];
          
          if (campaignData?.battlePlans) {
            // Check if this campaign has the battle plan
            const hasBattlePlan = campaignData.battlePlans.some(
              bp => bp.battle_plan_id === battlePlanId
            );
            
            if (hasBattlePlan) {
              foundCampaignId = campaignId;
              break;
            }
          }
        }

        if (!foundCampaignId) {
          // If not found in cache, try to fetch battle plans for each campaign
          const campaignStore = useCampaignStore.getState();
          const navigationStore = useNavigationStore.getState();
          
          for (const campaign of campaigns) {
            try {
              // Set the campaign context temporarily
              navigationStore.setSelectedCampaignId(branchId, campaign.boundary_id);
              campaignStore.syncWithNavigationStore();
              
              // Fetch battle plans for this campaign
              const battlePlans = await campaignStore.fetchBattlePlans();
              
              // Check if this campaign has the battle plan
              const hasBattlePlan = battlePlans.some(
                bp => bp.battle_plan_id === battlePlanId
              );
              
              if (hasBattlePlan) {
                foundCampaignId = campaign.boundary_id;
                break;
              }
            } catch (error) {
              // Continue to next campaign if this one fails
              console.warn(`Failed to fetch battle plans for campaign ${campaign.boundary_id}:`, error);
              continue;
            }
          }
        }

        if (foundCampaignId) {
          // Redirect to the correct campaign canvass battle plan index page
          router.replace({
            pathname: '/(main)/[branch]/campaign/[campaignId]/canvass/[battlePlanId]',
            params: {
              branch: branchId,
              campaignId: foundCampaignId,
              battlePlanId: battlePlanId,
            },
          });
        } else {
          setError(`Battle plan ${battlePlanId} not found in any campaign`);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error finding campaign for battle plan:', error);
        setError('Failed to find campaign for this battle plan');
        setLoading(false);
      }
    };

    findCampaignAndRedirect();
  }, [battlePlanId, router, getSelectedBranch, getCampaigns, cachedData]);

  if (loading) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" />
        <ThemedText style={{ marginTop: 16 }}>Finding campaign...</ThemedText>
      </ThemedView>
    );
  }

  if (error) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <ThemedText style={{ textAlign: 'center', color: 'red' }}>
          {error}
        </ThemedText>
        <ThemedText 
          style={{ textAlign: 'center', marginTop: 16, opacity: 0.7 }}
          onPress={() => router.back()}
        >
          Tap to go back
        </ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
      <ThemedText>Redirecting...</ThemedText>
    </ThemedView>
  );
}
